import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { GetGuestServicesResponse, RegisterGuestSessionResponse } from '@hotel-voice/shared';
import { useGuestSession, type SessionPhase } from '@/hooks/useGuestSession';
import { getGuestServices } from '@/services/firebase/callables';
import { toUserMessage } from '@/utils/errors';
import type { GuestLocation } from '@/utils/params';

/**
 * The guest's identity for the whole app: the validated session (hotel name,
 * room number), the QR location, the anonymous uid, and the services
 * catalogue the hotel has switched on.
 *
 * One provider above the router, so every page reads the same session and the
 * catalogue is fetched once — not once per page the guest opens. Pages never
 * ask the guest for a room number or a booking id; the server pins both to
 * the session when a request is filed.
 */

export type ServicesPhase = 'idle' | 'loading' | 'ready' | 'error';

export interface GuestContextValue {
  phase: SessionPhase;
  location: GuestLocation | null;
  uid: string | null;
  session: RegisterGuestSessionResponse | null;
  errorMessage: string | null;
  retry: () => void;
  submitPhone: (phone: string) => Promise<void>;

  services: GetGuestServicesResponse | null;
  servicesPhase: ServicesPhase;
  servicesError: string | null;
  reloadServices: () => void;
}

const GuestContext = createContext<GuestContextValue | null>(null);

export function GuestSessionProvider({ children }: { children: ReactNode }) {
  const guest = useGuestSession();
  const [servicesAttempt, setServicesAttempt] = useState(0);
  // The result is tagged with the key it was fetched for, and "loading" is
  // derived from whether the stored tag matches the current key — so a retry
  // or a new room never shows the previous catalogue as though it were this
  // one, and there is no setState at the top of the effect to get it wrong.
  const [loaded, setLoaded] = useState<{
    key: string;
    services: GetGuestServicesResponse | null;
    error: string | null;
  } | null>(null);

  const reloadServices = useCallback(() => setServicesAttempt((n) => n + 1), []);

  const { phase, location } = guest;
  const key =
    phase === 'ready' && location
      ? `${location.hotelId}/${location.roomId}#${servicesAttempt}`
      : null;

  useEffect(() => {
    if (!key || !location) return;
    let cancelled = false;

    getGuestServices({ hotelId: location.hotelId, roomId: location.roomId })
      .then((result) => {
        if (!cancelled) setLoaded({ key, services: result, error: null });
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoaded({ key, services: null, error: toUserMessage(error) });
      });

    return () => {
      cancelled = true;
    };
  }, [key, location]);

  const settled = loaded !== null && loaded.key === key;
  const servicesPhase: ServicesPhase = !key
    ? 'idle'
    : !settled
      ? 'loading'
      : loaded.error
        ? 'error'
        : 'ready';

  const value = useMemo<GuestContextValue>(
    () => ({
      ...guest,
      services: settled ? loaded.services : null,
      servicesPhase,
      servicesError: settled ? loaded.error : null,
      reloadServices,
    }),
    [guest, settled, loaded, servicesPhase, reloadServices],
  );

  return <GuestContext.Provider value={value}>{children}</GuestContext.Provider>;
}

export function useGuest(): GuestContextValue {
  const value = useContext(GuestContext);
  if (!value) {
    throw new Error('useGuest must be used inside GuestSessionProvider');
  }
  return value;
}

/**
 * The session and location, for pages that only render once both exist.
 * App gates on `phase === 'ready'` before mounting the router, so the throw
 * is a programming error, not a state a guest can reach.
 */
export function useReadyGuest(): GuestContextValue & {
  session: RegisterGuestSessionResponse;
  location: GuestLocation;
  uid: string;
} {
  const value = useGuest();
  if (!value.session || !value.location || !value.uid) {
    throw new Error('Guest session is not ready');
  }
  return value as GuestContextValue & {
    session: RegisterGuestSessionResponse;
    location: GuestLocation;
    uid: string;
  };
}
